// Safari/WebKit propagates Element.scrollIntoView() and HTMLElement.focus()
// across iframe boundaries. The messenger renders inside a position:fixed iframe
// docked to a corner of the host page, so when we scroll a message list to the
// bottom or focus an input, WebKit also scrolls the *host page* to "reveal" the
// already-visible iframe. The visible symptom is the host page drifting/jumping
// down every time a widget screen mounts or the conversation scrolls.
//
// The fix is to never ask the browser to reveal an element. We scroll the
// messenger's own scroll container directly (scrollTop never crosses frames) and
// always focus with { preventScroll: true }.

const isScrollable = (element: HTMLElement): boolean => {
  const { overflowY } = window.getComputedStyle(element);
  const canScrollY = overflowY === "auto" || overflowY === "scroll";
  return canScrollY && element.scrollHeight > element.clientHeight;
};

// Nearest ancestor that actually scrolls vertically, staying within this
// document (never returns the iframe/host page).
export const getScrollParent = (
  element: HTMLElement | null
): HTMLElement | null => {
  let current = element?.parentElement ?? null;
  while (current) {
    if (isScrollable(current)) {
      return current;
    }
    current = current.parentElement;
  }
  return null;
};

// Drop-in replacement for `anchor.scrollIntoView()` when the anchor is the last
// element of a message list (i.e. "scroll to bottom"). Scrolls only the anchor's
// own scroll container, so the host page is never touched.
export const scrollAnchorToBottom = (
  anchor: HTMLElement | null,
  behavior: ScrollBehavior = "smooth"
): void => {
  if (!anchor) {
    return;
  }

  const container = getScrollParent(anchor);
  if (!container) {
    return;
  }

  container.scrollTo({ top: container.scrollHeight, behavior });
};

// Drop-in replacement for `element.scrollIntoView({ block: "nearest" })`:
// scrolls only the element's own container, and only as far as needed. The
// container's padding counts as covered: the conversation pads its list by the
// floating composer (and announcement) that sit on top of it.
export const revealInScrollParent = (element: HTMLElement | null): void => {
  if (!element) {
    return;
  }
  const container = getScrollParent(element);
  if (!container) {
    return;
  }
  const box = element.getBoundingClientRect();
  const outer = container.getBoundingClientRect();
  const style = window.getComputedStyle(container);
  const view = {
    top: outer.top + (parseFloat(style.paddingTop) || 0),
    bottom: outer.bottom - (parseFloat(style.paddingBottom) || 0),
  };
  if (box.top < view.top) {
    container.scrollTop -= view.top - box.top + 12;
  } else if (box.bottom > view.bottom) {
    container.scrollTop += Math.min(
      box.bottom - view.bottom + 12,
      box.top - view.top - 12,
    );
  }
};

// Focus without letting WebKit scroll the (cross-frame) host page to the focused
// field. preventScroll is honored by all current browsers and harmlessly ignored
// by very old engines.
export const focusWithoutScroll = (element: HTMLElement | null): void => {
  if (!element) {
    return;
  }

  try {
    element.focus({ preventScroll: true });
  } catch {
    element.focus();
  }
};
